import type { UseQueryResult } from "@tanstack/react-query";
import { AlertOctagon, AlertTriangle, CheckCircle2, ChevronRight, Info, Loader2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import type {
  DashboardSummary,
  Operations,
  OperationsWeek,
  Shipments,
} from "@/api/warehouse_dashboard";
import type { TurnoverDays, TurnoverSummary } from "@/api/warehouse_turnover";
import { extractErrorMessage } from "@/lib/api-errors";
import { ruNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { fmtOperationValue, NO_VALUE, NO_VALUE_HINT } from "@/lib/warehouse-operations";
import {
  type Attention,
  type AttentionItem,
  buildAttention,
  counterpartyNote,
  findMetric,
  fmtPeriodLine,
  fmtRubExact,
  fmtRubShort,
  isNoOrganizationError,
  pickWeeks,
  SOURCE_TITLES,
} from "@/lib/warehouse-overview";
import { weekBars } from "@/lib/week-bars";
import { fmtDays, NO_EXPENSE_HINT } from "@/lib/warehouse-turnover";

import { DASHBOARD_TABS, dashboardTabPath } from "./tabs";
import { WeekBars } from "./WeekBars";

export interface OverviewQueries {
  summary: UseQueryResult<DashboardSummary>;
  turnover: UseQueryResult<TurnoverSummary>;
  operations: UseQueryResult<Operations>;
  shipments: UseQueryResult<Shipments>;
}

/** Период оборачиваемости на «Обзоре» — фиксирован (плитка «Без движения 90
 *  дн»); ключ запроса тот же, что у вкладки при том же периоде — кеш общий. */
export const OVERVIEW_TURNOVER_DAYS: TurnoverDays = 90;

type Fmt = (v: number | null) => string;
const fmtCount: Fmt = (v) => fmtOperationValue(v, "count");
const fmtShare: Fmt = (v) => fmtOperationValue(v, "share");

/**
 * Вкладка «Обзор» — контроль склада для руководителя: «Требует внимания»
 * сверху, строка-период (какая неделя крупно, с какой сравнение, какая идёт),
 * плитки ключевых показателей (последняя ПОЛНАЯ неделя крупно + столбцы 8
 * недель), отдельным рядом — запас (срез сейчас) и ссылки на вкладки.
 *
 * Четыре источника независимы: упавший ломает только свои плитки (и даёт
 * пункт во «Требует внимания»), остальные работают. Значения — как пришли:
 * ни дельт, ни процентов изменения экран не считает; «было» — число
 * позапрошлой недели из того же ответа рядом.
 */
export function OverviewTab({ q }: { q: OverviewQueries }): React.ReactElement {
  const attention = buildAttention({
    summary: q.summary,
    turnover: q.turnover,
    operations: q.operations,
    shipments: q.shipments,
  });

  const note = counterpartyNote(q.turnover.data);
  // Календарь один у обеих недельных ручек (`operations.calendar_weeks`).
  const weeks = q.operations.data?.weeks ?? q.shipments.data?.weeks;
  const period = weeks ? fmtPeriodLine(weeks) : null;

  return (
    <div className="space-y-3">
      <AttentionBlock attention={attention} />
      {note && <CounterpartyNote text={note} />}

      {period && (
        <p data-testid="overview-period" className="text-[13px] text-slate-700">
          {period}
        </p>
      )}
      <section
        aria-label="Показатели"
        className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <ShipmentsTile q={q.shipments} />
        <OpsTile
          q={q.operations}
          title="Собрано заказов покупателя"
          metric="assembled_customer_orders"
          unit="заказов"
          extraMetric="assembled_units"
          extra={(v) => `${fmtOperationValue(v, "units")} шт собрано (с перемещениями)`}
        />
        <OpsTile
          q={q.operations}
          title="ФБС WB: собрано заданий"
          metric="fbs_picked"
          unit="заданий"
          extraMetric="fbs_on_time_share"
          extra={(v) => `в срок: ${fmtShare(v)}`}
        />
        <OpsTile
          q={q.operations}
          title="ФБС Ozon: отправлений"
          metric="ozon_shipped"
          unit="отправлений"
          extraMetric="ozon_on_time_share"
          extra={(v) => `до отсечки: ${fmtShare(v)}`}
        />
        <OpsTile
          q={q.operations}
          title="Принято поставок, УПД"
          metric="received_upd"
          unit="УПД"
          extraMetric="received_units"
          extra={(v) => `${fmtOperationValue(v, "units")} шт`}
        />
        <OpsTile
          q={q.operations}
          title="Возвраты, шт"
          metric="returns_units"
          fmt={(v) => fmtOperationValue(v, "units")}
          unit="шт"
        />
      </section>
      <section aria-label="Запас" className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <StockTile q={q.turnover} />
        <NoMovementTile q={q.turnover} />
      </section>

      <nav aria-label="Подробнее" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className="text-muted-foreground">Подробнее:</span>
        {DASHBOARD_TABS.filter((t) => t.key !== "overview").map((t) => (
          <Link
            key={t.key}
            to={dashboardTabPath(t.key)}
            className="inline-flex items-center text-slate-700 underline-offset-2 hover:underline"
          >
            {t.title}
            <ChevronRight className="size-3.5" />
          </Link>
        ))}
      </nav>

      {q.operations.data && q.operations.data.notes.length > 0 && (
        <ul className="space-y-0.5 text-xs leading-5 text-muted-foreground">
          {q.operations.data.notes.map((n) => (
            <li key={n}>* {n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- «Требует внимания» ------------------------------------------------------

/** Товар у контрагентов — информационная строка, НЕ предупреждение: нейтральный
 *  стиль, ссылка на вкладку «Оборачиваемость» со списком. */
function CounterpartyNote({ text }: { text: string }): React.ReactElement {
  return (
    <Link
      to={dashboardTabPath("turnover")}
      data-testid="overview-counterparty-note"
      className="flex items-start gap-2 rounded-lg border bg-white px-3 py-1.5 text-[13px] leading-5 text-slate-700 hover:bg-slate-50"
    >
      <Info className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden="true" />
      <span className="min-w-0 flex-1">{text}</span>
      <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

function sourceList(keys: (keyof typeof SOURCE_TITLES)[]): string {
  return keys.map((k) => SOURCE_TITLES[k]).join(", ");
}

/** Список проблем, каждая — одна строка-ссылка на вкладку с подробностями.
 *  Пусто — одна зелёная строка «Всё в норме» С ПЕРЕЧНЕМ проверенного:
 *  «в норме» по источнику, который не ответил, было бы неправдой. */
function AttentionBlock({ attention }: { attention: Attention }): React.ReactElement {
  const { items, checked, pending } = attention;
  const pendingLine = pending.length > 0 && (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
      <Loader2 className="size-3.5 shrink-0 animate-spin" />
      проверяется: {sourceList(pending)}
    </div>
  );

  if (items.length === 0) {
    return (
      <section aria-label="Требует внимания" className="space-y-1">
        {checked.length > 0 && (
          <div
            data-testid="attention-ok"
            className="flex flex-wrap items-center gap-x-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[13px] text-emerald-800"
          >
            <CheckCircle2 className="size-4 shrink-0" />
            <span className="font-medium">Всё в норме</span>
            <span className="text-xs text-emerald-700/90">проверено: {sourceList(checked)}</span>
          </div>
        )}
        {pendingLine}
      </section>
    );
  }

  return (
    <section aria-label="Требует внимания" className="overflow-hidden rounded-lg border bg-white">
      <h2 className="border-b bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
        Требует внимания
      </h2>
      <ul className="divide-y">
        {items.map((it) => (
          <AttentionRow key={it.key} item={it} />
        ))}
      </ul>
      {pendingLine && <div className="border-t">{pendingLine}</div>}
    </section>
  );
}

function AttentionRow({ item }: { item: AttentionItem }): React.ReactElement {
  const critical = item.tone === "critical";
  const Icon = critical ? AlertOctagon : AlertTriangle;
  const body = (
    <>
      <Icon
        className={cn("mt-0.5 size-4 shrink-0", critical ? "text-rose-600" : "text-amber-600")}
        aria-label={critical ? "срочно" : "внимание"}
      />
      <span className="min-w-0 flex-1 sm:flex sm:items-baseline sm:gap-2">
        <span
          className={cn(
            "line-clamp-2 break-words sm:line-clamp-1",
            critical ? "font-medium text-rose-800" : "text-amber-900",
          )}
        >
          {item.text}
        </span>
        {item.hint && (
          <span
            className={cn(
              "block shrink-0 text-xs sm:max-w-[40%] sm:truncate",
              critical ? "text-rose-700" : "text-muted-foreground",
            )}
          >
            {item.hint}
          </span>
        )}
      </span>
      {item.tab && <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
    </>
  );
  const cls = cn(
    "flex items-start gap-2 px-3 py-1.5 text-[13px] leading-5",
    critical && "bg-rose-50/60",
  );
  const title = item.hint ? `${item.text} — ${item.hint}` : item.text;
  return (
    <li data-testid={`attention-${item.key}`} data-tone={item.tone}>
      {item.tab ? (
        <Link to={dashboardTabPath(item.tab)} className={cn(cls, "hover:bg-slate-50")} title={title}>
          {body}
        </Link>
      ) : (
        <div className={cls} title={title}>
          {body}
        </div>
      )}
    </li>
  );
}

// --- плитки ------------------------------------------------------------------

type Source = "1С" | "портал";

/** Оболочка плитки: подпись, источник мелко, тело по состоянию запроса.
 *  Ошибка — компактно внутри плитки (соседние работают), с «Повторить». */
function Tile({
  title,
  source,
  footer,
  q,
  children,
}: {
  title: string;
  source: Source;
  footer?: string | null;
  q: UseQueryResult<unknown>;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <article
      aria-label={title}
      className="flex min-w-0 flex-col gap-1 rounded-lg border bg-white px-4 py-3"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="truncate text-sm font-medium text-slate-700" title={title}>
          {title}
        </h3>
        <span className="shrink-0 text-[11px] text-muted-foreground">{source}</span>
      </header>
      <div className="flex min-h-[72px] flex-1 flex-col">
        {q.isLoading ? (
          <Loader2 className="m-auto size-5 animate-spin text-muted-foreground" />
        ) : q.error ? (
          <TileError error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          children
        )}
      </div>
      {footer && !q.error && (
        <footer className="truncate text-[11px] text-muted-foreground" title={footer}>
          {footer}
        </footer>
      )}
    </article>
  );
}

function TileError({ error, onRetry }: { error: unknown; onRetry: () => void }): React.ReactElement {
  const text = isNoOrganizationError(error)
    ? "Не задана организация для оборачиваемости (Системные настройки)"
    : extractErrorMessage(error, "Не удалось загрузить");
  return (
    <div role="alert" className="flex flex-1 flex-col items-start gap-1 text-xs text-rose-700">
      <span className="flex gap-1.5">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        <span className="line-clamp-3 break-words" title={text}>
          {text}
        </span>
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="mt-auto text-slate-700 underline underline-offset-2 hover:text-foreground"
      >
        Повторить
      </button>
    </div>
  );
}

/** Показатель без данных в ответе — плитка говорит об этом, а не рисует ноль. */
function Missing({ what }: { what: string }): React.ReactElement {
  return <p className="text-xs text-rose-700">{what}</p>;
}

/**
 * Тело недельной плитки: крупно — последняя ПОЛНАЯ неделя, под ним своё
 * уточнение (`extra`) и «пред. неделя» числом (без процентов), ниже — столбцы
 * всех недель окна (`WeekBars`): выделена показанная, текущая — штриховкой.
 * Даты недель — в строке-периоде над плитками и под столбцами, в плитке не
 * повторяются.
 */
function WeeklyBody({
  weeks,
  values,
  fmt,
  unit,
  extra,
}: {
  weeks: OperationsWeek[];
  values: (number | null)[];
  fmt: Fmt;
  unit: string;
  extra?: (i: number) => React.ReactNode;
}): React.ReactElement {
  const pick = pickWeeks(weeks);
  if (pick.last === null) return <Missing what="В ответе нет полной недели" />;
  const v = values[pick.last] ?? null;
  return (
    <div className="flex flex-1 flex-col gap-1">
      <span
        className={cn(
          "text-3xl font-semibold leading-9 tabular-nums",
          v === null && "text-muted-foreground",
        )}
        title={v === null ? NO_VALUE_HINT : undefined}
      >
        {fmt(v)}
      </span>
      {extra && <div className="text-[13px] text-slate-700">{extra(pick.last)}</div>}
      {pick.prev !== null && (
        <div className="text-xs text-muted-foreground">
          пред. неделя: {fmt(values[pick.prev] ?? null)}
        </div>
      )}
      <div className="mt-auto pt-2">
        <WeekBars bars={weekBars(weeks, values, fmt, { unit })} />
      </div>
    </div>
  );
}

function ShipmentsTile({ q }: { q: UseQueryResult<Shipments> }): React.ReactElement {
  const d = q.data;
  return (
    <Tile title="Отгружено со склада, шт" source="1С" q={q}>
      {d && (
        <WeeklyBody
          weeks={d.weeks}
          values={d.units}
          fmt={(v) => fmtOperationValue(v, "units")}
          unit="шт"
          extra={(i) => (
            <span title={`себестоимость ${fmtRubExact(d.cost_rub[i] ?? 0)}`}>
              {fmtRubShort(d.cost_rub[i] ?? 0)} себест. · {ruNum.format(d.documents[i] ?? 0)} расх.
            </span>
          )}
        />
      )}
    </Tile>
  );
}

function OpsTile({
  q,
  title,
  metric,
  extraMetric,
  extra,
  fmt = fmtCount,
  unit,
}: {
  q: UseQueryResult<Operations>;
  title: string;
  metric: string;
  /** Единица в подсказке столбца: «14–20.09: 21 шт». */
  unit: string;
  extraMetric?: string;
  extra?: (v: number | null) => string;
  fmt?: Fmt;
}): React.ReactElement {
  const d = q.data;
  const main = d ? findMetric(d, metric) : null;
  const second = d && extraMetric ? findMetric(d, extraMetric) : null;
  return (
    <Tile title={title} source="портал" q={q}>
      {d &&
        (!main || (extraMetric && !second) ? (
          <Missing what={`В ответе нет показателя ${!main ? metric : extraMetric}`} />
        ) : (
          <WeeklyBody
            weeks={d.weeks}
            values={main.values}
            fmt={fmt}
            unit={unit}
            extra={second && extra ? (i) => extra(second.values[i] ?? null) : undefined}
          />
        ))}
    </Tile>
  );
}

/** Плитки запаса — не недельные: срез остатка сейчас, расход — за период запроса. */
function turnoverFooter(d: TurnoverSummary | undefined): string | null {
  return d ? `${d.organization.name} · срез сейчас · расход за ${d.days} дн` : null;
}

function StockTile({ q }: { q: UseQueryResult<TurnoverSummary> }): React.ReactElement {
  const d = q.data;
  return (
    <Tile title="Запас организации" source="1С" footer={turnoverFooter(d)} q={q}>
      {d && (
        <div className="flex flex-1 flex-col gap-0.5">
          <span
            className="text-3xl font-semibold leading-9 tabular-nums"
            title={fmtRubExact(d.totals.stock_sum)}
          >
            {fmtRubShort(d.totals.stock_sum)}
          </span>
          <div className="flex flex-wrap gap-x-4 text-[13px] text-slate-700">
            <Days label="оборачиваемость" value={d.totals.turnover_days} />
            <Days label="запас" value={d.totals.cover_days} />
          </div>
        </div>
      )}
    </Tile>
  );
}

function Days({ label, value }: { label: string; value: number | null }): React.ReactElement {
  return (
    <span title={value === null ? NO_EXPENSE_HINT : undefined}>
      {label}: {value === null ? NO_VALUE : `${fmtDays(value)} дн`}
    </span>
  );
}

function NoMovementTile({ q }: { q: UseQueryResult<TurnoverSummary> }): React.ReactElement {
  const d = q.data;
  return (
    <Tile
      title={`Без движения ${OVERVIEW_TURNOVER_DAYS} дн`}
      source="1С"
      footer={turnoverFooter(d)}
      q={q}
    >
      {d && (
        <div className="flex flex-1 flex-col gap-0.5">
          <span
            className="text-3xl font-semibold leading-9 tabular-nums"
            title={fmtRubExact(d.totals.no_movement_stock_sum)}
          >
            {fmtRubShort(d.totals.no_movement_stock_sum)}
          </span>
          <span className="text-[13px] text-slate-700">
            {ruNum.format(d.totals.no_movement_count)} поз. с остатком без расхода
          </span>
        </div>
      )}
    </Tile>
  );
}
